import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Mail,
  Eye,
  ExternalLink,
  MessageSquare,
  Clock,
  CheckCircle2,
  Search,
} from "lucide-react";
import { toast } from "sonner";

import { useConsultingData } from "../../features/consulting/useConsultingData";
import { useConsultingStore } from "../../lib/consulting/store";
import { StatusBadge } from "../../lib/consulting/ui";

const STATUS_OPTIONS = [
  "unread",
  "proposal_submitted",
  "declined",
  "in_progress",
  "completed",
];

export default function ConsultingRequestsPage() {
  const {
    rows,
    loading,
    setStatus: setStatusApi,
    setAssignee: setAssigneeApi,
    setNotes: setNotesApi,
    toggleUnread: toggleUnreadApi,
  } = useConsultingData();

  const store =
    typeof useConsultingStore === "function" ? useConsultingStore() : null;
  const q = store?.q ?? "";
  const setQuery = store?.setQuery ?? (() => {});
  const statusFilter = store?.statusFilter ?? "all";
  const setStatusFilter = store?.setStatusFilter ?? (() => {});
  const unreadOnly = store?.unreadOnly ?? false;
  const setUnreadOnly = store?.setUnreadOnly ?? (() => {});

  const items = useMemo(() => {
    let list = rows;
    if (q) {
      const n = q.toLowerCase();
      list = list.filter(
        (r) =>
          r.message?.toLowerCase().includes(n) ||
          r.notes?.toLowerCase().includes(n) ||
          r.assignee?.toLowerCase().includes(n) ||
          r.name?.toLowerCase().includes(n) ||
          r.email?.toLowerCase().includes(n),
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => r.status === statusFilter);
    }
    if (unreadOnly) {
      list = list.filter((r) => r.status === "unread");
    }
    return list;
  }, [rows, q, statusFilter, unreadOnly]);

  const onStatusChange = (id: string, value: string) => {
    setStatusApi(id, value);
    toast.success("Status updated");
  };

  const onAssigneeBlur = (id: string, value: string) => {
    setAssigneeApi(id, value.trim());
  };

  const onNotesBlur = (id: string, value: string) => {
    setNotesApi(id, value);
  };

  const onToggleUnread = (id: string) => {
    toggleUnreadApi(id);
  };

  const formatStatus = (s: string) => s.replace(/_/g, " ");

  const unreadCount = rows.filter((r) => r.status === "unread").length;
  const inProgressCount = rows.filter((r) => r.status === "in_progress").length;
  const completedCount = rows.filter((r) => r.status === "completed").length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-500/10 text-green-600">Completed</Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-500/10 text-blue-600">In Progress</Badge>
        );
      case "read":
        return <Badge variant="secondary">Read</Badge>;
      default:
        return <Badge className="bg-amber-500/10 text-amber-600">Unread</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Consulting Requests</h1>
        <p className="text-muted-foreground">
          Manage incoming consulting inquiries from users
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total Requests
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rows.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Unread</CardTitle>
            <Mail className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {unreadCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {inProgressCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {completedCount}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by message, user, notes..."
                value={q}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Status Filter
              </span>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {formatStatus(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="unread"
                checked={unreadOnly}
                onCheckedChange={(checked) => setUnreadOnly(checked as boolean)}
              />
              <label
                htmlFor="unread"
                className="text-sm flex items-center gap-1"
              >
                <Mail className="h-4 w-4" />
                Unread only
              </label>
            </div>
          </div>

          {loading ? (
            <div className="text-base text-muted-foreground py-10 text-center">
              Loading requests...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48">USER</TableHead>
                  <TableHead>MESSAGE / DATE</TableHead>
                  <TableHead className="w-36">STATUS</TableHead>
                  <TableHead className="w-32">ASSIGNEE</TableHead>
                  <TableHead className="w-48">NOTES</TableHead>
                  <TableHead className="w-20 text-right">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const isUnread = r.status === "unread";

                  return (
                    <TableRow
                      key={r.id}
                      className={isUnread ? "bg-amber-50/50" : ""}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-1">
                            <span className="text-muted-foreground">👤</span>
                            {r.name || "Unknown User"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {r.email || "No email"}
                          </div>
                          {r.user_id && (
                            <Link to={`/admin/deals?userId=${r.user_id}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-1"
                              >
                                View Deals{" "}
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <Mail
                            className={`h-4 w-4 mt-1 ${isUnread ? "text-amber-500" : "text-muted-foreground"}`}
                          />
                          <div>
                            <div
                              className={`font-medium ${isUnread ? "text-gray-900 font-semibold" : ""}`}
                            >
                              {r.message}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(
                                r.created_at ?? Date.now(),
                              ).toLocaleString()}
                            </div>
                            {isUnread && (
                              <Badge className="mt-1 bg-amber-100 text-amber-700 border-amber-200">
                                Unread
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <Select
                            value={r.status}
                            onValueChange={(value) =>
                              onStatusChange(r.id, value)
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {formatStatus(s)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {typeof StatusBadge === "function" ? (
                            <StatusBadge status={r.status} />
                          ) : (
                            getStatusBadge(r.status)
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Assignee"
                          defaultValue={r.assignee ?? ""}
                          onBlur={(e) => onAssigneeBlur(r.id, e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Add notes..."
                          defaultValue={r.notes ?? ""}
                          onBlur={(e) => onNotesBlur(r.id, e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onToggleUnread(r.id)}
                          title={isUnread ? "Mark as Read" : "Mark as Unread"}
                        >
                          {isUnread ? (
                            <Eye className="h-4 w-4 text-gray-700" />
                          ) : (
                            <Mail className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-muted-foreground"
                    >
                      No consulting requests match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
